
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Function to get all therapists with detailed information
export async function getAllTherapists() {
  try {
    console.log("Starting getAllTherapists function...");
    
    // First, get all users with therapist role
    const { data: therapistRoles, error: therapistRolesError } = await supabase
      .from('user_roles')
      .select('user_id, role')
      .eq('role', 'therapist');
      
    if (therapistRolesError) {
      console.error("Error fetching therapist roles:", therapistRolesError);
      toast.error("Failed to fetch therapist roles");
      return [];
    }
    
    console.log("Found therapist roles:", therapistRoles?.length || 0);
    
    if (!therapistRoles || therapistRoles.length === 0) {
      console.log("No therapist roles found");
      return [];
    }
    
    // Get detailed profile information for each therapist
    const therapistIds = therapistRoles.map(role => role.user_id);
    console.log("Therapist IDs to fetch:", therapistIds.length, therapistIds);
    
    const { data: therapistProfiles, error: profilesError } = await supabase
      .from('user_profiles')
      .select('*');
      
    if (profilesError) {
      console.error("Error fetching therapist profiles:", profilesError);
      toast.error("Failed to fetch therapist profiles");
      return [];
    }
    
    // Filter profiles to only include those with therapist roles
    const filteredProfiles = therapistProfiles?.filter(profile => 
      therapistIds.includes(profile.user_id)
    ) || [];
    
    console.log("Therapist profiles fetched:", filteredProfiles.length);
    
    // Get active client count for each therapist
    const { data: relationships, error: relError } = await supabase
      .from('client_therapist_relationships')
      .select('therapist_id, status');
      
    const clientCounts: Record<string, number> = {};
    
    if (!relError && relationships) {
      relationships.forEach(rel => {
        if (rel.status === 'active') {
          clientCounts[rel.therapist_id] = (clientCounts[rel.therapist_id] || 0) + 1;
        }
      });
      console.log("Client counts per therapist:", clientCounts);
    } else {
      console.error("Error fetching client-therapist relationships:", relError);
    }
    
    // Check for profiles without matching role
    const profileUserIds = new Set(filteredProfiles.map(p => p.user_id) || []);
    const missingProfiles = therapistIds.filter(id => !profileUserIds.has(id));
    
    if (missingProfiles.length > 0) {
      console.warn("Warning: Some therapists have roles but no profiles:", missingProfiles);
    }
    
    // Create a combined result with all available data
    const result = [];
    
    // First add therapists with profiles
    if (filteredProfiles) {
      for (const profile of filteredProfiles) {
        result.push({
          ...profile,
          email: profile.email || `${profile.user_id.substring(0, 8)}@example.com`,
          client_count: clientCounts[profile.user_id] || 0
        });
      }
    }
    
    // Then add therapists that have roles but no profiles
    for (const id of missingProfiles) {
      result.push({
        user_id: id,
        id: id,
        email: `${id.substring(0, 8)}@example.com`,
        first_name: null,
        last_name: null,
        client_count: clientCounts[id] || 0,
        created_at: new Date().toISOString() // placeholder
      });
    }
    
    console.log("Final therapist list:", result.length);
    return result;
  } catch (error) {
    console.error("Error fetching all therapists:", error);
    toast.error("Failed to load therapist data");
    return [];
  }
}
